import { useState, useRef, useCallback } from 'react'
import { useExecution } from '../../hooks/useExecution'
import AttachmentCell from './AttachmentCell'
import clsx from 'clsx'

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending',  color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/25',   dot: 'bg-blue-400'   },
  { value: 'pass',    label: 'Pass',     color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/25', dot: 'bg-emerald-400' },
  { value: 'fail',    label: 'Fail',     color: 'text-red-400',     bg: 'bg-red-500/15',     border: 'border-red-500/25',     dot: 'bg-red-400'     },
  { value: 'blocked', label: 'Blocked',  color: 'text-amber-400',   bg: 'bg-amber-500/15',   border: 'border-amber-500/25',   dot: 'bg-amber-400'   },
  { value: 'skipped', label: 'Skipped',  color: 'text-gray-400',    bg: 'bg-gray-500/15',    border: 'border-gray-500/25',    dot: 'bg-gray-400'    },
]
const statusMeta = (v) => STATUS_OPTIONS.find(s => s.value === v) ?? STATUS_OPTIONS[0]

// ── Column widths ─────────────────────────────────────────────────────────────
const COL = {
  id:       'w-[80px] min-w-[80px]',
  title:    'w-[220px] min-w-[180px]',
  qa_name:  'w-[140px] min-w-[120px]',
  qa_date:  'w-[120px] min-w-[110px]',
  status:   'w-[110px] min-w-[100px]',
  notes:    'w-[200px] min-w-[160px]',
  defect:   'w-[110px] min-w-[90px]',
  attach:   'w-[140px] min-w-[120px]',
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * TestPlanGrid
 *
 * Props:
 *   executionId — number
 */
export default function TestPlanGrid({ executionId }) {
  const {
    execution, grouped, progress, passRate,
    loading, saving,
    saveRow, uploadAttachment, deleteAttachment, updateStatus,
  } = useExecution(executionId)

  const [expandedRoles, setExpandedRoles] = useState({})
  const [activeCell, setActiveCell]       = useState(null)  // { rowId, field }

  const toggleRole = (role) =>
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }))

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  if (!execution) return (
    <div className="text-center py-16 text-white/40">Execution not found.</div>
  )

  // Default all roles to expanded
  const isExpanded = (role) => expandedRoles[role] !== false

  return (
    <div className="flex flex-col h-full min-h-0">

      {/* ── Progress bar ───────────────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between gap-6 flex-shrink-0">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-white/60">Progress</span>
            <span className="text-xs font-bold tabular-nums text-white">{progress}%</span>
          </div>
          <div className="h-2 bg-white/8 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats pills */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {grouped.map(g => null) && null /* pre-compute totals */}
          {[
            { label: 'Total',   value: execution.total_cases,   color: 'text-white/60' },
            { label: 'Passed',  value: execution.passed_cases,  color: 'text-emerald-400' },
            { label: 'Failed',  value: execution.failed_cases,  color: 'text-red-400' },
            { label: 'Pending', value: execution.pending_cases, color: 'text-blue-400' },
          ].map(s => (
            <div key={s.label} className="text-center px-3 py-1.5 bg-white/4 border border-white/8 rounded-xl">
              <div className={clsx('text-lg font-bold tabular-nums leading-none', s.color)}>{s.value ?? 0}</div>
              <div className="text-[9px] text-white/30 font-medium uppercase tracking-wider mt-0.5">{s.label}</div>
            </div>
          ))}
          {passRate !== null && (
            <div className="text-center px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <div className="text-lg font-bold tabular-nums text-emerald-400 leading-none">{passRate}%</div>
              <div className="text-[9px] text-emerald-400/60 font-medium uppercase tracking-wider mt-0.5">Pass rate</div>
            </div>
          )}
        </div>

        {/* Export PDF button */}
        <a
          href={`/api/executions/${executionId}/export-pdf`}
          target="_blank"
          rel="noreferrer"
          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-semibold hover:bg-purple-500/25 transition-all"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Export PDF
        </a>
      </div>

      {/* ── Scrollable grid ────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto min-h-0">

        {/* Column headers — sticky */}
        <div className="sticky top-0 z-20 flex items-stretch bg-cosmic-900/95 backdrop-blur-sm border-b border-white/10">
          <HeaderCell cls={COL.id}      label="Test ID"   />
          <HeaderCell cls={COL.title}   label="Test Case" />
          <HeaderCell cls={COL.qa_name} label="QA Person" />
          <HeaderCell cls={COL.qa_date} label="QA Date"   />
          <HeaderCell cls={COL.status}  label="Result"    />
          <HeaderCell cls={COL.notes}   label="Notes / Actual Result" />
          <HeaderCell cls={COL.defect}  label="Defect ID" />
          <HeaderCell cls={COL.attach}  label="Evidence"  />
        </div>

        {/* Role sections */}
        {grouped.map(group => (
          <div key={group.role}>

            {/* Role section header — acts like a spreadsheet group row */}
            <button
              type="button"
              onClick={() => toggleRole(group.role)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-900/40 to-blue-900/20 border-b border-t border-white/8 hover:from-purple-900/60 transition-all sticky top-[42px] z-10"
            >
              <svg className={clsx('w-4 h-4 text-purple-400 transition-transform flex-shrink-0', !isExpanded(group.role) && '-rotate-90')}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
              </svg>
              <span className="font-display font-bold text-white text-sm">{group.role}</span>
              <span className="text-xs text-white/35">{group.total} test{group.total !== 1 ? 's' : ''}</span>

              {/* Mini progress for this role */}
              <div className="ml-auto flex items-center gap-2">
                {[
                  { v: group.passed,  c: 'text-emerald-400 bg-emerald-500/15', l: '✓' },
                  { v: group.failed,  c: 'text-red-400 bg-red-500/15',         l: '✗' },
                  { v: group.pending, c: 'text-blue-400 bg-blue-500/15',       l: '…' },
                ].map(s => s.v > 0 && (
                  <span key={s.l} className={clsx('text-[11px] font-bold px-2 py-0.5 rounded-full', s.c)}>
                    {s.l} {s.v}
                  </span>
                ))}
              </div>
            </button>

            {/* Rows */}
            {isExpanded(group.role) && group.rows.map((row, rowIdx) => (
              <GridRow
                key={row.id}
                row={row}
                rowIdx={rowIdx}
                saving={!!saving[row.id]}
                activeCell={activeCell}
                onFocus={setActiveCell}
                onChange={(field, value) => saveRow(row.id, { [field]: value })}
                onUpload={(file, caption) => uploadAttachment(row.id, file, caption)}
                onDeleteAttachment={(attId) => deleteAttachment(row.id, attId)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Header cell ───────────────────────────────────────────────────────────────
function HeaderCell({ cls, label }) {
  return (
    <div className={clsx(cls, 'px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-white/35 border-r border-white/6 select-none')}>
      {label}
    </div>
  )
}

// ── Grid row ──────────────────────────────────────────────────────────────────
function GridRow({ row, rowIdx, saving, activeCell, onFocus, onChange, onUpload, onDeleteAttachment }) {
  const isFail = row.status === 'fail'
  const sm     = statusMeta(row.status)

  return (
    <div className={clsx(
      'flex items-stretch border-b border-white/5 group transition-colors duration-100',
      rowIdx % 2 === 0 ? 'bg-white/[0.01]' : 'bg-transparent',
      'hover:bg-purple-500/[0.04]',
      isFail && 'bg-red-500/[0.04] hover:bg-red-500/[0.07]'
    )}>

      {/* Test ID */}
      <div className={clsx(COL.id, 'px-3 py-2.5 border-r border-white/5 flex items-center')}>
        <span className="text-[10px] font-mono text-purple-400/70 truncate">{row.test_case_id ?? `#${row.id}`}</span>
        {saving && (
          <div className="ml-1 w-2.5 h-2.5 border border-purple-400/40 border-t-purple-400 rounded-full animate-spin flex-shrink-0" />
        )}
      </div>

      {/* Test Case Title — expandable on click */}
      <TestCaseCell cls={COL.title} row={row} />

      {/* QA Person Name */}
      <InlineEditCell
        cls={COL.qa_name}
        value={row.qa_person_name ?? ''}
        placeholder="Enter name…"
        isActive={activeCell?.rowId === row.id && activeCell?.field === 'qa_person_name'}
        onFocus={() => onFocus({ rowId: row.id, field: 'qa_person_name' })}
        onBlur={() => onFocus(null)}
        onChange={v => onChange('qa_person_name', v)}
      />

      {/* QA Date */}
      <DateCell
        cls={COL.qa_date}
        value={row.qa_date ?? ''}
        onChange={v => onChange('qa_date', v)}
      />

      {/* Status (Pass/Fail/Pending dropdown) */}
      <StatusCell
        cls={COL.status}
        value={row.status}
        onChange={v => onChange('status', v)}
      />

      {/* Notes */}
      <InlineEditCell
        cls={COL.notes}
        value={row.notes ?? ''}
        placeholder="Actual result / notes…"
        multiline
        isActive={activeCell?.rowId === row.id && activeCell?.field === 'notes'}
        onFocus={() => onFocus({ rowId: row.id, field: 'notes' })}
        onBlur={() => onFocus(null)}
        onChange={v => onChange('notes', v)}
      />

      {/* Defect ID */}
      <InlineEditCell
        cls={COL.defect}
        value={row.defect_id ?? ''}
        placeholder="JIRA-000"
        isActive={activeCell?.rowId === row.id && activeCell?.field === 'defect_id'}
        onFocus={() => onFocus({ rowId: row.id, field: 'defect_id' })}
        onBlur={() => onFocus(null)}
        onChange={v => onChange('defect_id', v)}
        mono
      />

      {/* Attachment cell — only fully active when status = fail */}
      <AttachmentCell
        cls={COL.attach}
        attachments={row.attachments ?? []}
        isFail={isFail}
        rowId={row.id}
        onUpload={onUpload}
        onDelete={onDeleteAttachment}
      />
    </div>
  )
}

// ── Cell components ───────────────────────────────────────────────────────────

function TestCaseCell({ cls, row }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className={clsx(cls, 'px-3 py-2.5 border-r border-white/5 cursor-pointer')} onClick={() => setExpanded(!expanded)}>
      <p className={clsx('text-[11px] font-medium text-white', !expanded && 'truncate')}>{row.test_case_title}</p>
      {expanded && row.expected_result && (
        <p className="text-[10px] text-white/40 mt-1 leading-relaxed">{row.expected_result}</p>
      )}
    </div>
  )
}

function InlineEditCell({ cls, value, placeholder, onChange, onFocus, onBlur, isActive, multiline, mono }) {
  const [local, setLocal] = useState(value)
  const ref = useRef(null)

  // Sync if parent value changes (e.g. after server response)
  // Only sync when not actively editing
  if (!isActive && local !== value) setLocal(value)

  const commit = () => {
    if (local !== value) onChange(local)
    onBlur()
  }

  if (multiline) {
    return (
      <div className={clsx(cls, 'border-r border-white/5 relative')}>
        <textarea
          ref={ref}
          value={local}
          placeholder={placeholder}
          rows={2}
          className={clsx(
            'w-full h-full px-3 py-2.5 bg-transparent text-[11px] text-white/80 placeholder-white/20 resize-none outline-none transition-colors',
            isActive && 'bg-purple-500/8',
            mono && 'font-mono'
          )}
          onFocus={onFocus}
          onBlur={commit}
          onChange={e => setLocal(e.target.value)}
        />
      </div>
    )
  }

  return (
    <div className={clsx(cls, 'border-r border-white/5 relative')}>
      <input
        ref={ref}
        type="text"
        value={local}
        placeholder={placeholder}
        className={clsx(
          'w-full h-full px-3 py-2.5 bg-transparent text-[11px] text-white/80 placeholder-white/20 outline-none transition-colors',
          isActive && 'bg-purple-500/8',
          mono && 'font-mono'
        )}
        onFocus={onFocus}
        onBlur={commit}
        onChange={e => setLocal(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && commit()}
      />
    </div>
  )
}

function DateCell({ cls, value, onChange }) {
  return (
    <div className={clsx(cls, 'border-r border-white/5')}>
      <input
        type="date"
        value={value}
        className="w-full h-full px-3 py-2.5 bg-transparent text-[11px] text-white/70 outline-none cursor-pointer"
        onChange={e => onChange(e.target.value)}
      />
    </div>
  )
}

function StatusCell({ cls, value, onChange }) {
  const [open, setOpen] = useState(false)
  const sm = statusMeta(value)

  return (
    <div className={clsx(cls, 'border-r border-white/5 relative')} onClick={() => setOpen(!open)}>
      <button
        type="button"
        className={clsx(
          'w-full h-full px-3 py-2.5 flex items-center gap-2 text-[11px] font-bold cursor-pointer transition-colors',
          sm.color
        )}
      >
        <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', sm.dot)} />
        {sm.label}
        <svg className="w-3 h-3 ml-auto text-white/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 z-30 w-36 mt-0.5 rounded-xl border border-white/12 bg-cosmic-900/98 backdrop-blur-xl shadow-2xl overflow-hidden py-1"
          onMouseLeave={() => setOpen(false)}>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(s.value); setOpen(false) }}
              className={clsx(
                'w-full flex items-center gap-2.5 px-3 py-2 text-[11px] font-semibold transition-colors',
                value === s.value ? s.bg + ' ' + s.color : 'text-white/50 hover:bg-white/5 hover:text-white'
              )}
            >
              <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', s.dot)} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
